import React, { useState } from 'react';
import {
  Calendar,
  AlertTriangle,
  Clock,
  Trash2,
  Package,
  DollarSign,
  TrendingDown,
  Search,
  Filter,
  Download,
  RefreshCw,
  Plus,
  Eye,
  Edit,
  CheckCircle,
  XCircle,
  BarChart3,
  Activity,
  Target,
  Zap,
  Building,
  Users,
  ArrowRightLeft,
  FileText,
  Send
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { comprehensiveMedicationDatabase, ComprehensiveMedication } from '../../data/comprehensiveMedicationDatabase';
import { format, addDays, subDays, differenceInDays, isAfter, isBefore } from 'date-fns';
import StatCard from '../../components/StatCard';

interface ExpiryItem extends ComprehensiveMedication {
  batchNumber: string;
  expiryDate: string;
  quantityInBatch: number;
  costValue: number;
  retailValue: number;
  daysUntilExpiry: number;
  expiryStatus: 'expired' | 'critical' | 'warning' | 'normal';
  location: string;
  supplier: string;
  receivedDate: string;
  disposalRequired: boolean;
  canReturn: boolean;
  returnDeadline?: string;
  nearPatientDispensing: boolean;
  specialDisposal: boolean;
}

interface ExpiryAlert {
  id: string;
  medicationId: string;
  medicationName: string;
  batchNumber: string;
  expiryDate: string;
  quantity: number;
  type: 'expired' | 'expiring_soon' | 'return_deadline' | 'disposal_required';
  severity: 'critical' | 'high' | 'medium';
  message: string;
  recommendedAction: string;
  estimatedLoss: number;
  daysUntilExpiry: number;
}

interface DisposalRecord {
  id: string;
  medicationName: string;
  batchNumber: string;
  quantity: number;
  expiryDate: string;
  disposalDate: string;
  disposalMethod: 'incineration' | 'return_to_supplier' | 'pharmaceutical_waste' | 'special_handling';
  disposalCost: number;
  authorizedBy: string;
  witnessedBy?: string;
  certificateNumber?: string;
  reason: string;
}

const ExpiryManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('daysUntilExpiry');
  const [viewMode, setViewMode] = useState<'table' | 'calendar' | 'analytics'>('table');
  const [showAlerts, setShowAlerts] = useState(true);
  const [timeframe, setTimeframe] = useState<string>('30_days');

  // Generate comprehensive expiry data
  const generateExpiryItems = (): ExpiryItem[] => {
    const items: ExpiryItem[] = [];
    
    comprehensiveMedicationDatabase.forEach((med, index) => {
      // Generate 2-4 batches per medication with different expiry dates
      const batchCount = Math.floor(Math.random() * 3) + 2;
      
      for (let i = 0; i < batchCount; i++) {
        const daysUntilExpiry = Math.floor(Math.random() * 730) - 30; // -30 to 700 days
        const expiryDate = format(addDays(new Date(), daysUntilExpiry), 'yyyy-MM-dd');
        const quantityInBatch = Math.floor(Math.random() * 200) + 20;
        
        let expiryStatus: 'expired' | 'critical' | 'warning' | 'normal';
        if (daysUntilExpiry < 0) expiryStatus = 'expired';
        else if (daysUntilExpiry <= 30) expiryStatus = 'critical';
        else if (daysUntilExpiry <= 90) expiryStatus = 'warning';
        else expiryStatus = 'normal';

        items.push({
          ...med,
          id: `${med.id}-batch-${i}`,
          batchNumber: `LOT${String(index + 1).padStart(3, '0')}${String(i + 1).padStart(2, '0')}`,
          expiryDate,
          quantityInBatch,
          costValue: quantityInBatch * med.costOfGoodsSold,
          retailValue: quantityInBatch * med.retailPrice,
          daysUntilExpiry,
          expiryStatus,
          location: ['Main Pharmacy', 'Cold Storage', 'Controlled Drugs Safe', 'Dispensary', 'Warehouse'][Math.floor(Math.random() * 5)],
          supplier: ['Sigma Healthcare', 'API', 'Symbion', 'EBOS Group', 'Pharma Direct'][Math.floor(Math.random() * 5)],
          receivedDate: format(subDays(new Date(), Math.floor(Math.random() * 365) + 30), 'yyyy-MM-dd'),
          disposalRequired: expiryStatus === 'expired',
          canReturn: daysUntilExpiry > -7 && Math.random() > 0.3,
          returnDeadline: daysUntilExpiry > -7 && Math.random() > 0.3 ? 
            format(addDays(new Date(), Math.floor(Math.random() * 14) + 1), 'yyyy-MM-dd') : undefined,
          nearPatientDispensing: Math.random() > 0.8,
          specialDisposal: med.schedule === 'S8' || ['Chemotherapy', 'Cytotoxic'].some(term => med.name.includes(term))
        });
      }
    });
    
    return items;
  };

  const [expiryItems] = useState<ExpiryItem[]>(generateExpiryItems());

  // Generate expiry alerts
  const expiryAlerts: ExpiryAlert[] = expiryItems
    .filter(item => item.expiryStatus === 'expired' || item.expiryStatus === 'critical' || 
                   (item.canReturn && item.returnDeadline && differenceInDays(new Date(item.returnDeadline), new Date()) <= 3))
    .map(item => ({
      id: `alert-${item.id}`,
      medicationId: item.id,
      medicationName: item.name,
      batchNumber: item.batchNumber,
      expiryDate: item.expiryDate,
      quantity: item.quantityInBatch,
      type: item.expiryStatus === 'expired' ? 'expired' :
            item.canReturn && item.returnDeadline && differenceInDays(new Date(item.returnDeadline), new Date()) <= 3 ? 'return_deadline' :
            'expiring_soon',
      severity: item.expiryStatus === 'expired' ? 'critical' : 
               item.daysUntilExpiry <= 7 ? 'critical' : 'high',
      message: item.expiryStatus === 'expired' 
        ? `Expired ${Math.abs(item.daysUntilExpiry)} days ago - disposal required`
        : item.canReturn && item.returnDeadline 
        ? `Return deadline in ${differenceInDays(new Date(item.returnDeadline), new Date())} days`
        : `Expires in ${item.daysUntilExpiry} days`,
      recommendedAction: item.expiryStatus === 'expired' 
        ? 'Schedule disposal immediately'
        : item.canReturn 
        ? 'Process return to supplier'
        : 'Monitor for near-expiry dispensing',
      estimatedLoss: item.expiryStatus === 'expired' ? item.costValue : 0,
      daysUntilExpiry: item.daysUntilExpiry
    }));

  // Mock disposal records
  const disposalRecords: DisposalRecord[] = [
    {
      id: 'disp-001',
      medicationName: 'Paracetamol 500mg',
      batchNumber: 'LOT001A',
      quantity: 150,
      expiryDate: '2024-08-15',
      disposalDate: '2024-09-01',
      disposalMethod: 'return_to_supplier',
      disposalCost: 0,
      authorizedBy: 'Dr. Sarah Johnson',
      reason: 'Expired stock - returned within deadline'
    },
    {
      id: 'disp-002',
      medicationName: 'OxyContin 20mg',
      batchNumber: 'LOT045S8',
      quantity: 25,
      expiryDate: '2024-07-30',
      disposalDate: '2024-08-20',
      disposalMethod: 'pharmaceutical_waste',
      disposalCost: 125.50,
      authorizedBy: 'Dr. Sarah Johnson',
      witnessedBy: 'Mark Thompson, PharmTech',
      certificateNumber: 'PWD-2024-0820-001',
      reason: 'S8 controlled substance - witnessed disposal'
    }
  ];

  const filteredItems = expiryItems
    .filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.batchNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.supplier.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      const matchesStatus = selectedStatus === 'all' || item.expiryStatus === selectedStatus;
      const matchesLocation = selectedLocation === 'all' || item.location === selectedLocation;
      
      // Timeframe filter
      let matchesTimeframe = true;
      if (timeframe !== 'all') {
        const days = parseInt(timeframe.split('_')[0]);
        matchesTimeframe = item.daysUntilExpiry <= days;
      }
      
      return matchesSearch && matchesCategory && matchesStatus && matchesLocation && matchesTimeframe;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'daysUntilExpiry':
          return a.daysUntilExpiry - b.daysUntilExpiry;
        case 'expiryStatus':
          const statusOrder = { expired: 0, critical: 1, warning: 2, normal: 3 };
          return statusOrder[a.expiryStatus] - statusOrder[b.expiryStatus];
        case 'costValue':
          return b.costValue - a.costValue;
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

  const categories = expiryItems.reduce((acc: string[], item) => {
    if (!acc.includes(item.category)) {
      acc.push(item.category);
    }
    return acc;
  }, []);
  const locations = expiryItems.reduce((acc: string[], item) => {
    if (!acc.includes(item.location)) {
      acc.push(item.location);
    }
    return acc;
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'expired': return 'bg-red-100 text-red-800 border-red-200';
      case 'critical': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'normal': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'expired': return <XCircle className="h-4 w-4 text-red-600" />;
      case 'critical': return <AlertTriangle className="h-4 w-4 text-orange-600" />;
      case 'warning': return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'normal': return <CheckCircle className="h-4 w-4 text-green-600" />;
      default: return <Package className="h-4 w-4 text-gray-600" />;
    }
  };

  // Analytics data
  const expiryStatusData = [
    { status: 'Expired', count: expiryItems.filter(i => i.expiryStatus === 'expired').length, color: '#EF4444' },
    { status: 'Critical (≤30 days)', count: expiryItems.filter(i => i.expiryStatus === 'critical').length, color: '#F97316' },
    { status: 'Warning (≤90 days)', count: expiryItems.filter(i => i.expiryStatus === 'warning').length, color: '#EAB308' },
    { status: 'Normal (>90 days)', count: expiryItems.filter(i => i.expiryStatus === 'normal').length, color: '#10B981' }
  ];

  const monthlyExpiryData = Array.from({ length: 12 }, (_, i) => {
    const month = format(addDays(new Date(), i * 30), 'MMM yyyy');
    const startDate = addDays(new Date(), i * 30);
    const endDate = addDays(new Date(), (i + 1) * 30);
    
    const expiringItems = expiryItems.filter(item => {
      const expiry = new Date(item.expiryDate);
      return expiry >= startDate && expiry < endDate;
    });
    
    return {
      month,
      count: expiringItems.length,
      value: expiringItems.reduce((sum, item) => sum + item.costValue, 0)
    };
  });

  const totalExpiredValue = expiryItems.filter(item => item.expiryStatus === 'expired').reduce((sum, item) => sum + item.costValue, 0);
  const criticalItems = expiryItems.filter(item => item.expiryStatus === 'critical').length;
  const returnableItems = expiryItems.filter(item => item.canReturn).length;
  const disposalRequired = expiryItems.filter(item => item.disposalRequired).length;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Expiry Management
          </h1>
          <p className="text-gray-600 mt-2">Comprehensive medication expiry tracking with automated alerts and disposal management</p>
        </div>
        <div className="flex space-x-3">
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
            <Download className="h-4 w-4" />
            <span>Export Report</span>
          </button>
          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>Schedule Disposal</span>
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Expired Stock Value"
          value={`$${totalExpiredValue.toLocaleString()}`}
          subtitle="Requires immediate disposal"
          icon={<XCircle className="h-6 w-6" />}
          color="text-red-600"
          bgGradient="bg-gradient-to-br from-red-500 to-red-600"
        />
        <StatCard
          title="Critical Items"
          value={criticalItems}
          subtitle="Expiring within 30 days"
          icon={<AlertTriangle className="h-6 w-6" />}
          color="text-orange-600"
          bgGradient="bg-gradient-to-br from-orange-500 to-yellow-500"
        />
        <StatCard
          title="Returnable Items"
          value={returnableItems}
          subtitle="Can be returned to supplier"
          icon={<ArrowRightLeft className="h-6 w-6" />}
          color="text-blue-600"
          bgGradient="bg-gradient-to-br from-blue-500 to-indigo-600"
        />
        <StatCard
          title="Disposal Required"
          value={disposalRequired}
          subtitle="Items awaiting disposal"
          icon={<Trash2 className="h-6 w-6" />}
          color="text-purple-600"
          bgGradient="bg-gradient-to-br from-purple-500 to-pink-600"
        />
      </div>

      {/* Expiry Alerts */}
      {showAlerts && expiryAlerts.length > 0 && (
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-6 w-6 text-red-600" />
              <div>
                <h3 className="text-xl font-bold text-gray-900">Expiry Alerts ({expiryAlerts.length})</h3>
                <p className="text-gray-600 text-sm">Items requiring immediate attention</p>
              </div>
            </div>
            <button 
              onClick={() => setShowAlerts(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="h-5 w-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {expiryAlerts.slice(0, 6).map((alert) => (
              <div key={alert.id} className={`p-4 rounded-xl border ${
                alert.severity === 'critical' ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'
              }`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-900">{alert.medicationName}</h4>
                    <p className="text-sm text-gray-600">Batch: {alert.batchNumber}</p>
                    <p className="text-sm text-gray-600">{alert.message}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    alert.severity === 'critical' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'
                  }`}>
                    {alert.severity.toUpperCase()}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                  <div>
                    <p className="text-gray-600">Quantity:</p>
                    <p className="font-medium">{alert.quantity} units</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Expiry Date:</p>
                    <p className="font-medium">{format(new Date(alert.expiryDate), 'dd/MM/yyyy')}</p>
                  </div>
                </div>
                
                {alert.estimatedLoss > 0 && (
                  <div className="mb-3">
                    <p className="text-sm text-gray-600">Estimated Loss:</p>
                    <p className="font-medium text-red-600">${alert.estimatedLoss.toFixed(2)}</p>
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-600">{alert.recommendedAction}</p>
                  <div className="flex space-x-2">
                    <button className="text-purple-600 hover:text-purple-700 text-sm font-medium">
                      Take Action
                    </button>
                    <button className="text-gray-600 hover:text-gray-700 text-sm font-medium">
                      Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* View Mode Toggle and Filters */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('table')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                viewMode === 'table' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Table View
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                viewMode === 'calendar' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Calendar View
            </button>
            <button
              onClick={() => setViewMode('analytics')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                viewMode === 'analytics' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Analytics
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search medications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="expired">Expired</option>
            <option value="critical">Critical (≤30 days)</option>
            <option value="warning">Warning (≤90 days)</option>
            <option value="normal">Normal (&gt;90 days)</option>
          </select>

          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Locations</option>
            {locations.map(location => (
              <option key={location} value={location}>{location}</option>
            ))}
          </select>

          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Timeframes</option>
            <option value="0_days">Expired</option>
            <option value="7_days">Next 7 days</option>
            <option value="30_days">Next 30 days</option>
            <option value="90_days">Next 90 days</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="daysUntilExpiry">Days Until Expiry</option>
            <option value="expiryStatus">Expiry Status</option>
            <option value="costValue">Cost Value</option>
            <option value="name">Name A-Z</option>
          </select>

          <button className="flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Filter className="h-4 w-4" />
            <span>More</span>
          </button>
        </div>
      </div>

      {/* Analytics View */}
      {viewMode === 'analytics' && (
        <div className="space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Expiry Status Distribution */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Expiry Status Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={expiryStatusData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="count"
                    label={({ status, count }) => `${status}: ${count}`}
                  >
                    {expiryStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Monthly Expiry Forecast */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">12-Month Expiry Forecast</h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={monthlyExpiryData}>
                  <defs>
                    <linearGradient id="colorExpiry" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [
                    name === 'count' ? `${value} items` : `$${value.toLocaleString()}`,
                    name === 'count' ? 'Items Expiring' : 'Value at Risk'
                  ]} />
                  <Area 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#8B5CF6" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorExpiry)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Recent Disposal Records */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Recent Disposal Records</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Medication</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Batch</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Quantity</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Disposal Method</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Date</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Cost</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Authorized By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {disposalRecords.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50/50">
                      <td className="px-6 py-4 font-medium text-gray-900">{record.medicationName}</td>
                      <td className="px-6 py-4 text-gray-900">{record.batchNumber}</td>
                      <td className="px-6 py-4 text-gray-900">{record.quantity}</td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 text-sm font-medium rounded-full bg-blue-100 text-blue-800">
                          {record.disposalMethod.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-900">{format(new Date(record.disposalDate), 'dd/MM/yyyy')}</td>
                      <td className="px-6 py-4 text-gray-900">${record.disposalCost.toFixed(2)}</td>
                      <td className="px-6 py-4 text-gray-900">{record.authorizedBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Medication</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Batch</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Expiry Date</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Days Until Expiry</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Quantity</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Cost Value</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Location</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50/50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{item.name}</p>
                        <p className="text-sm text-gray-600">{item.genericName}</p>
                        <p className="text-sm text-gray-500">{item.brandName}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-900">{item.batchNumber}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(item.expiryStatus)}
                        <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(item.expiryStatus)}`}>
                          {item.expiryStatus.charAt(0).toUpperCase() + item.expiryStatus.slice(1)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-900">{format(new Date(item.expiryDate), 'dd/MM/yyyy')}</td>
                    <td className="px-6 py-4">
                      <span className={`font-medium ${
                        item.daysUntilExpiry < 0 ? 'text-red-600' :
                        item.daysUntilExpiry <= 7 ? 'text-orange-600' :
                        item.daysUntilExpiry <= 30 ? 'text-yellow-600' : 'text-gray-900'
                      }`}>
                        {item.daysUntilExpiry < 0 ? `${Math.abs(item.daysUntilExpiry)} days ago` : `${item.daysUntilExpiry} days`}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-900">{item.quantityInBatch}</td>
                    <td className="px-6 py-4 font-medium text-gray-900">${item.costValue.toLocaleString()}</td>
                    <td className="px-6 py-4 text-gray-900">{item.location}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                          <Eye className="h-4 w-4" />
                        </button>
                        {item.canReturn && (
                          <button className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg">
                            <ArrowRightLeft className="h-4 w-4" />
                          </button>
                        )}
                        {item.disposalRequired && (
                          <button className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                        <button className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-100 rounded-lg">
                          <FileText className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <p>Showing {filteredItems.length} of {expiryItems.length} batches</p>
        <div className="flex items-center space-x-4">
          <p>Total Value at Risk: ${filteredItems.reduce((sum, item) => sum + item.costValue, 0).toLocaleString()}</p>
          <p>Expired Items: {filteredItems.filter(item => item.expiryStatus === 'expired').length}</p>
          <p>Critical Items: {filteredItems.filter(item => item.expiryStatus === 'critical').length}</p>
        </div>
      </div>
    </div>
  );
};

export default ExpiryManagement;
